// Named-query-result extractor.
//
// TWO syntaxes are accepted, side by side. The result tab is titled with the
// captured name; the marker is stripped from the SQL we actually send to MySQL.
//
// 1) Inline tilde — Ninocat-original, terse, easy to type:
//
//      ~Get Manager List~SELECT * FROM tuser WHERE level='Manager';
//
//    Lives at the very start of the statement (after leading ws + comments).
//    Tilde isn't a meaningful statement-leading character in SQL (it's a unary
//    bitwise NOT inside expressions), so this syntax never collides.
//
// 2) Comment annotation — portable, looks like a regular SQL comment so pasting
//    the query into DataGrip / DBeaver / Workbench just renders it as a no-op
//    comment instead of a syntax error:
//
//      -- @name: Get Manager List
//      SELECT * FROM tuser WHERE level='Manager';
//
//    Variants accepted: `--` or `#` line-comment markers; the `@` is optional;
//    the `name` keyword is case-insensitive. The comment must START with
//    `[@]name:` after the marker — so "`-- The name: blah`" doesn't trigger.
//
// If BOTH a tilde and a name comment appear in the same statement's preamble,
// the tilde wins (it's the more explicit form and the original feature).
//
// Each statement is processed independently (caller has already `;`-split), so
// every statement carries its own name annotation that only applies to it.

// extractStatementName(sql) → { name: string|null, sql: string }
//   name  the user-provided result-tab label (trimmed); null if no marker
//   sql   the SQL with the marker removed; other leading comments preserved.
//         For a comment-form name we replace the whole annotation line with a
//         bare newline so MySQL error line numbers still match the editor.
export function extractStatementName(sql) {
  if (typeof sql !== 'string' || sql.length === 0) {
    return { name: null, sql };
  }
  // Walk the preamble (leading ws + comments) and collect line-comment ranges
  // so the second-pass can scan them for an annotation. We need both
  //   • the first non-ws/non-comment cursor (for tilde detection), AND
  //   • each line-comment span so we can rewrite the matched annotation line.
  const lineComments = []; // [{ start, end (excl., past \n if present), body }]
  let cursor = 0;
  const n = sql.length;
  while (cursor < n) {
    const c = sql[cursor];
    if (c === ' ' || c === '\t' || c === '\n' || c === '\r') { cursor++; continue; }
    if (c === '-' && sql[cursor + 1] === '-') {
      const bodyStart = cursor + 2;
      const nl = sql.indexOf('\n', bodyStart);
      const lineEnd = nl < 0 ? n : nl;
      lineComments.push({
        start: cursor,
        end: nl < 0 ? n : nl + 1,
        body: sql.slice(bodyStart, lineEnd),
      });
      cursor = nl < 0 ? n : nl + 1;
      continue;
    }
    if (c === '#') {
      const bodyStart = cursor + 1;
      const nl = sql.indexOf('\n', bodyStart);
      const lineEnd = nl < 0 ? n : nl;
      lineComments.push({
        start: cursor,
        end: nl < 0 ? n : nl + 1,
        body: sql.slice(bodyStart, lineEnd),
      });
      cursor = nl < 0 ? n : nl + 1;
      continue;
    }
    if (c === '/' && sql[cursor + 1] === '*') {
      // Block comments are tolerated as preamble content but NOT scanned for
      // annotations (the multi-line form would need its own parser). Block
      // comments in the preamble are preserved untouched.
      const end = sql.indexOf('*/', cursor + 2);
      cursor = end < 0 ? n : end + 2;
      continue;
    }
    break;
  }

  // ── Tilde form (wins when both are present) ──
  if (cursor < n && sql[cursor] === '~') {
    const nameStart = cursor + 1;
    let nameEnd = nameStart;
    while (nameEnd < n && sql[nameEnd] !== '~' && sql[nameEnd] !== '\n') {
      nameEnd++;
    }
    if (nameEnd < n && sql[nameEnd] === '~') {
      const name = sql.slice(nameStart, nameEnd).trim();
      if (name) {
        const before = sql.slice(0, cursor);
        const after  = sql.slice(nameEnd + 1);
        return { name, sql: before + after };
      }
    }
  }

  // ── Comment form: scan the preamble's line-comment bodies for an
  // annotation `[@]name: <value>` (case-insensitive). The comment must START
  // with that token after the marker, so `-- The name: blah` doesn't match.
  // We replace the matched comment LINE with a bare newline so MySQL error
  // line-numbers track the editor.
  const NAME_RE = /^\s*@?name\s*:\s*(.+?)\s*$/i;
  for (const lc of lineComments) {
    const m = NAME_RE.exec(lc.body);
    if (m && m[1].trim()) {
      const name = m[1].trim();
      // Keep one '\n' so subsequent line numbers don't shift. If the comment
      // ended at EOF without a newline, we just elide it (nothing to track
      // line-wise — the statement is past).
      const replacement = lc.end > lc.start && sql[lc.end - 1] === '\n' ? '\n' : '';
      return {
        name,
        sql: sql.slice(0, lc.start) + replacement + sql.slice(lc.end),
      };
    }
  }
  return { name: null, sql };
}
